/* eslint-disable react/jsx-props-no-spreading */
import { FormControl, FormHelperText, InputLabel } from "@mui/material";
import { useController } from "react-hook-form";

import { CommonSelect, CommonMultiSelect } from "./CommonSelect";

import type { SelectNode } from "../../common/type_defs";
import type { SelectProps } from "@mui/material";
import type { ReactElement } from "react";
import type { FieldPath, FieldValues, UseControllerProps } from "react-hook-form";

type AdcSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = UseControllerProps<TFieldValues, TName> & {
  /** Additional settings */
  config?: {
    /**
     * Whether to display error messages below the input field.
     * If `true`, message displays.
     * @defaultValue `false`
     */
    displayErrorMessage?: boolean;
    isMulti?: boolean;
    /**
     * An array of Selectable options. Each option should be an object that has `value`, and shold have `label` property.
     * However an array of string or number also acceptable. In that case, `value` and `label` match the value of each element.
     * Note that each value must be unique.
     */
    options: Array<{ value: string; label: string }>;
  };
  /** Settings for MUI elements */
  muiProps?: {
    /**
     * Settings for Select inside AdcSelect
     *
     * API: {@link https://mui.com/material-ui/api/select/}
     */
    selectProps?: SelectProps;
  };
};

/**
 * MUI Select component linked to React Hook Form.
 * Type arguments are optional, but specifying them provides powerful type checking and type inference.
 * @typeParam TFieldValues - Type of the form.
 * @typeParam TName - Field name.
 * @example
 * ```
 * type FormData = {
 *   menu: string;
 * };
 *
 * <AdcSelect<FormData, 'menu'>
 *   name='menu'
 *   control={control}
 *   rules={{
 *     mandatory_field: 'Select menu.'
 *   }}
 *   config={{
 *     displayErrorMessage: true,
 *     options: ['Beef', 'Pork', 'Chicken']
 *   }}
 *   muiProps={{
 *     selectProps: {
 *       label: 'Menu',
 *       fullWidth: true
 *     }
 *   }}
 * />
 * ```
 */
export const AdcSelect: <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: AdcSelectProps<TFieldValues, TName>
) => ReactElement = (props) => {
  const { muiProps, config, ...others } = props;
  const { selectProps } = muiProps ?? {};
  const { field, fieldState } = useController(others);

  const handleMultiSelectChange = (v: SelectNode[]) => {
    if (!v) {
      field.onChange([] as any);
    } else {
      const ary = v.map((d) => d.value);
      field.onChange(ary as any);
    }
  };
  const handleSelectChange = (v: SelectNode) => {
    if (!v) {
      field.onChange("" as any);
    } else {
      field.onChange(v.value as any);
    }
  };

  return (
    <FormControl
      error={!!fieldState.error}
      style={{ width: selectProps?.fullWidth ? "100%" : undefined }}
    >
      <InputLabel id={`mui-select-label-${selectProps?.label}`}>{selectProps?.label}</InputLabel>
      {config?.isMulti ? (
        <CommonMultiSelect
          id={field.name}
          inputRef={field.ref}
          name={field.name}
          value={field.value}
          options={config?.options ?? []}
          onChanged={handleMultiSelectChange}
          sxx={{ minWidth: 150 }}
        />
      ) : (
        <CommonSelect
          id="field.name"
          inputRef={field.ref}
          name={field.name}
          value={field.value}
          options={config?.options ?? []}
          onChanged={handleSelectChange}
          sxx={{ minWidth: 150 }}
        />
      )}

      {/*
      <Select
        // Attention to ther order of parameters passing
        {...selectProps}
        labelId={`mui-select-label-${selectProps?.label}`}
        inputRef={field.ref}
        id={field.name}
        name={field.name}
        value={field.value}
        error={!!fieldState.error}
        onChange={field.onChange as any}
        onBlur={field.onBlur}
      >
        {
          <MenuItem disabled value="">
            <em>Please Select Items</em>
          </MenuItem>
        }
        {config?.options.map((option) => {
          return (
            <MenuItem
              key={typeof option === 'object' ? option.value : option}
              value={typeof option === 'object' ? option.value : option}
            >
              {typeof option === 'object'
                ? option.label ?? option.value
                : option}
            </MenuItem>
          );
        })}
      </Select>
      */}
      <FormHelperText id={`${field.name}-helper-text`}>
        {config?.displayErrorMessage && fieldState.error?.message}
      </FormHelperText>
    </FormControl>
  );
};
